import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Grid, Divider,
  Tooltip, Collapse, IconButton
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { signatureService } from '../services/signatureService';
import axios from 'axios';

function CryptoAuditTrail() {
  const [entityType, setEntityType] = useState('verification_session');
  const [entityId, setEntityId] = useState('');
  const [signatures, setSignatures] = useState([]);
  const [dveAttestations, setDveAttestations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [expandedAttestation, setExpandedAttestation] = useState(null);

  const isDveSearch = entityType === 'dve_attestation';

  const handleLookup = async () => {
    if (!entityId.trim()) return;
    try {
      setLoading(true);
      setError(null);
      setVerifyResult(null);
      setDveAttestations([]);
      setSignatures([]);
      setExpandedAttestation(null);

      if (isDveSearch) {
        // Query attestations via ELM backend proxy (data from results_summary)
        const response = await axios.get(`/api/verification/attestations/session/${entityId.trim()}`);
        setDveAttestations(response.data.attestations || []);
      } else {
        const data = await signatureService.getSignaturesForEntity(entityType, entityId);
        setSignatures(data ?? []);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (signatureId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await signatureService.verifySignature(signatureId, { verify: true });
      setVerifyResult({ signatureId, ...result });
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDveVerify = async (attestation) => {
    try {
      setLoading(true);
      setError(null);
      // Verify via ELM backend proxy (server-to-server to dVE)
      const response = await axios.post(
        `/api/verification/attestations/${attestation.attestation_id}/verify`
      );
      setVerifyResult({
        signatureId: attestation.attestation_id,
        ...response.data
      });
    } catch (err) {
      setVerifyResult({
        signatureId: attestation.attestation_id,
        valid: false,
        error: err.response?.data?.error || err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDveExport = async (attestationId) => {
    try {
      // Export via ELM backend proxy (server-to-server to dVE)
      const response = await axios.get(`/api/verification/attestations/${attestationId}/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation-${attestationId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  };

  const decisionColor = (decision) => {
    switch (decision) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'flagged': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Cryptographic Audit Trail</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Immutable tamper-evident signatures for finalized reports, verification sessions, and dVE attestations
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <TextField fullWidth label="Source" value={entityType} onChange={(e) => setEntityType(e.target.value)} select SelectProps={{ native: true }}>
              <option value="verification_session">ELM: Verification Session</option>
              <option value="e_ledger">ELM: E-Ledger</option>
              <option value="report">ELM: Report</option>
              <option value="dve_attestation">dVE: Verification Attestation</option>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label={isDveSearch ? 'Session ID (UUID)' : 'Entity ID (UUID)'}
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
            />
          </Grid>
          <Grid item xs={3}>
            <Button variant="contained" fullWidth onClick={handleLookup} disabled={loading} sx={{ height: 56 }}>
              {loading ? <CircularProgress size={24} /> : isDveSearch ? 'Load Attestations' : 'Load Signatures'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {verifyResult && (
        <Alert severity={verifyResult.valid ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setVerifyResult(null)}>
          {isDveSearch ? 'Attestation' : 'Signature'} {verifyResult.signatureId?.slice(0, 8)}...: {verifyResult.valid ? 'Valid - content integrity confirmed' : 'Invalid - content may have been tampered with'}
          {verifyResult.details && (
            <Box sx={{ mt: 1 }}>
              Hash match: {verifyResult.details.hash_match ? 'Yes' : 'No'} |
              Signature valid: {verifyResult.details.signature_valid ? 'Yes' : 'No'} |
              {verifyResult.details.signer && <> Signed by: {verifyResult.details.signer} |</>}
              {verifyResult.details.attested_by && <> Attested by: {verifyResult.details.attested_by} |</>}
              Algorithm: {verifyResult.details.algorithm}
            </Box>
          )}
        </Alert>
      )}

      {/* ELM Signatures Table */}
      {!isDveSearch && signatures.length > 0 && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Lifecycle</TableCell>
                <TableCell>Algorithm</TableCell>
                <TableCell>Signer</TableCell>
                <TableCell>Content Hash</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signatures.map((sig) => (
                <TableRow key={sig.cryptographic_signature_id}>
                  <TableCell>{new Date(sig.timestamp_utc).toLocaleString()}</TableCell>
                  <TableCell><Chip label={sig.lifecycle_type} size="small" color={sig.lifecycle_type === 'external_certification' ? 'primary' : 'default'} /></TableCell>
                  <TableCell><Chip label={sig.signature_algorithm} size="small" variant="outlined" /></TableCell>
                  <TableCell>{sig.signer_identity}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {sig.content_hash}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleVerify(sig.cryptographic_signature_id)}>Verify</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* dVE Attestations Table */}
      {isDveSearch && dveAttestations.length > 0 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FingerprintIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              dVE Verification Attestations ({dveAttestations.length})
            </Typography>
          </Box>
          <Divider />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Attested At</TableCell>
                <TableCell>Entity Type</TableCell>
                <TableCell>Entity ID</TableCell>
                <TableCell>Decision</TableCell>
                <TableCell align="right">Confidence</TableCell>
                <TableCell>Algorithm</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dveAttestations.map((att) => {
                const isExpanded = expandedAttestation === att.attestation_id;
                return (
                  <React.Fragment key={att.attestation_id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpandedAttestation(isExpanded ? null : att.attestation_id)}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{new Date(att.attested_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip label={att.entity_type?.replace(/_/g, ' ')} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {att.entity_id?.slice(0, 8)}...
                      </TableCell>
                      <TableCell>
                        <Chip label={att.verification_decision} size="small" color={decisionColor(att.verification_decision)} />
                      </TableCell>
                      <TableCell align="right">
                        {att.confidence_score != null ? `${(parseFloat(att.confidence_score) * 100).toFixed(1)}%` : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip label={att.signature_algorithm} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Verify attestation">
                          <Button size="small" onClick={() => handleDveVerify(att)}>Verify</Button>
                        </Tooltip>
                        <Tooltip title="Export for certification">
                          <IconButton size="small" onClick={() => handleDveExport(att.attestation_id)}>
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Attestation ID</Typography>
                                <Typography variant="body2" fontFamily="monospace">{att.attestation_id}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Workflow ID</Typography>
                                <Typography variant="body2" fontFamily="monospace">{att.verification_workflow_id}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Signing Key</Typography>
                                <Typography variant="body2" fontFamily="monospace">{att.signing_key_id}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Content Hash (SHA-256)</Typography>
                                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>{att.content_hash}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Attested By</Typography>
                                <Typography variant="body2">{att.attested_by}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Lifecycle</Typography>
                                <Typography variant="body2">{att.lifecycle_type}</Typography>
                              </Grid>
                              {att.agent_reasoning && (
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography variant="caption" color="text.secondary">AI Reasoning</Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{att.agent_reasoning}</Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {!isDveSearch && signatures.length === 0 && !loading && entityId && (
        <Alert severity="info">No signatures found for this entity</Alert>
      )}

      {isDveSearch && dveAttestations.length === 0 && !loading && entityId && (
        <Alert severity="info">No dVE attestations found for this session</Alert>
      )}
    </Box>
  );
}

export default CryptoAuditTrail;
